import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminVolume } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Chip } from "@/ui/Chip";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { absoluteTime, bytes, shortTime } from "@/lib/format";
import { meterLamp } from "@/ui/Meter";
export const Route = createFileRoute("/_app/admin/volumes")({
  component: VolumesScreen,
});

/**
 * Volumes, fullest first.
 *
 * A volume filling up is a customer outage with about a week of warning, and
 * the warning was going nowhere: the agent has been reporting capacity and
 * usage on every heartbeat since volumes existed, and nothing read either
 * column.
 *
 * Sorted by the fraction rather than by bytes. A 2 GiB volume at 95% needs
 * somebody sooner than a 500 GiB volume at 40%, and sorting by used bytes puts
 * them the other way round.
 */
function VolumesScreen() {
  const volumes = useQuery({
    queryKey: ["platform-volumes"],
    queryFn: ({ signal }) =>
      api.get<{ volumes: AdminVolume[] | null }>("/admin/volumes", { limit: 300 }, signal),
    refetchInterval: 60000,
  });

  const list = volumes.data?.volumes ?? [];
  const tight = list.filter((v) => fraction(v) !== undefined && fraction(v)! >= 0.85);

  return (
    <div className="flex flex-col gap-4">
      {tight.length > 0 && (
        <div className="rounded-xl border border-[var(--danger)] bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)] px-4 py-3">
          <p className="text-[14px] font-medium text-[var(--danger)]">
            {tight.length === 1 ? "One volume is nearly full" : `${tight.length} volumes are nearly full`}
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            {tight
              .slice(0, 5)
              .map((v) => `${v.organisation_name}/${v.name} (${percent(v)})`)
              .join(", ")}
          </p>
        </div>
      )}

      <Panel
        padded={false}
      >
        {volumes.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={5} columns={6} />
          </div>
        ) : volumes.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={volumes.error} onRetry={() => void volumes.refetch()} />
          </div>
        ) : list.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title="No volumes" description="No service has one attached." />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[20%]">Volume</Th>
                  <Th width="w-[17%]">Tenant</Th>
                  <Th width="w-[16%]">Service</Th>
                  <Th width="w-[16%]">Used</Th>
                  <Th width="w-[10%]" numeric>
                    Size
                  </Th>
                  <Th width="w-[11%]">State</Th>
                  <Th width="w-[10%]" align="right">
                    Backup
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {list.map((v) => {
                  const f = fraction(v);
                  return (
                    <Tr
                      key={v.id}
                      className={
                        f !== undefined && f >= 0.85
                          ? "bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)]"
                          : f !== undefined && f >= 0.7
                            ? "bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)]"
                            : undefined
                      }
                    >
                      <Td clip title={v.mount_path} secondary={v.mount_path}>
                        {v.name}
                      </Td>
                      <Td clip title={v.organisation_name}>
                        <Link
                          to="/admin/$orgId"
                          params={{ orgId: v.organisation_id }}
                          className="hover:text-[var(--primary)]"
                        >
                          {v.organisation_name}
                        </Link>
                      </Td>
                      <Td clip title={v.service_name || v.service_id}>
                        {v.service_name || "—"}
                      </Td>
                      {/* A bar as well as a figure. The whole point of this
                          screen is noticing one row among two hundred, and a
                          column of percentages does not do that on its own. */}
                      <Td
                        title={
                          v.used_bytes !== undefined && v.capacity_bytes !== undefined
                            ? `${bytes(v.used_bytes)} of ${bytes(v.capacity_bytes)}`
                            : "The agent has not reported usage for this volume."
                        }
                      >
                        {f === undefined ? (
                          <span className="text-[var(--text-muted)]">not reported</span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-[var(--surface-2)]">
                              <span
                                className="block h-full rounded-full"
                                // Inline in a row rather than a Meter: a
                                // labelled meter here would change the row
                                // height, and the staff tables are held to one.
                                // The colour still comes from the one rule.
                                style={{
                                  width: `${Math.min(100, Math.round(f * 100))}%`,
                                  background: meterLamp(f),
                                }}
                              />
                            </span>
                            <span className="tabular-nums">{percent(v)}</span>
                          </span>
                        )}
                      </Td>
                      <Td numeric className="mono text-[13px]">
                        {v.size_gib}Gi
                      </Td>
                      <Td clip title={v.phase || v.state}>
                        <Chip tone={v.state === "ready" ? "up" : "warn"}>{v.state}</Chip>
                      </Td>
                      <Td align="right" title={v.backup_at ? absoluteTime(v.backup_at) : undefined}>
                        {v.backup_at ? shortTime(v.backup_at) : "—"}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  );
}

/** How full, or undefined when the agent has not said. */
function fraction(v: AdminVolume): number | undefined {
  if (!v.capacity_bytes || v.used_bytes === undefined) return undefined;
  return v.used_bytes / v.capacity_bytes;
}

function percent(v: AdminVolume): string {
  const f = fraction(v);
  return f === undefined ? "—" : `${Math.round(f * 100)}%`;
}
